// Right-hand "Proposal order" panel for the New Business builder — drag to set the prospect-page order.
import { useMemo, useState } from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, X, Package, ListOrdered, RotateCcw, LayoutTemplate, EyeOff, ExternalLink, Loader2 } from "lucide-react";
import { productKey, bundleKey, recommendedLayoutOrder, resolveLayoutOrder, buildLayoutBlocks, FIXED_BLOCKS } from "./proposalLayout";
import ProposalPagePreview from "./ProposalPagePreview";
import { roiModes } from "./roiMath";

function OrderRow({ item, idx, total, onMove, onRemove, active, onHover }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.55 : 1 };
  const cls = `rdo-row is-${item.type} ${isDragging ? "is-dragging" : ""} ${active ? "is-active" : ""} ${item.hidden ? "is-hidden" : ""}`;
  return (
    <div ref={setNodeRef} style={style} className={cls} id={`rdo-${item.key}`} onMouseEnter={() => onHover(item.key)} onMouseLeave={() => onHover(null)} data-testid={`rdo-row-${item.key}`}>
      <span className="rdo-grip" {...attributes} {...listeners} title="Drag to reorder" data-testid={`rdo-grip-${item.key}`}><GripVertical size={14} /></span>
      <span className="rdo-num">{idx + 1}</span>
      <div className="rdo-main">
        <span className="rdo-name">
          {item.type === "bundle" && <Package size={12} />}
          {item.type === "fixed" && <LayoutTemplate size={12} />}
          {item.name}
        </span>
        <span className="rdo-meta">
          {item.hidden ? <><EyeOff size={10} /> {item.hidden}</> : <>{item.meta}{item.price != null ? ` · £${item.price}` : ""}</>}
        </span>
      </div>
      <div className="rdo-actions">
        <button type="button" className="rdo-arrow" disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} title="Move up" data-testid={`rdo-up-${item.key}`}><ChevronUp size={13} /></button>
        <button type="button" className="rdo-arrow" disabled={idx === total - 1} onClick={() => onMove(idx, idx + 1)} title="Move down" data-testid={`rdo-down-${item.key}`}><ChevronDown size={13} /></button>
        {item.type !== "fixed" && (
          <button type="button" className="rdo-remove" onClick={() => onRemove(item)} title={item.type === "bundle" ? "Remove package" : "Untick product"} data-testid={`rdo-remove-${item.key}`}><X size={13} /></button>
        )}
      </div>
    </div>
  );
}

export default function ProposalOrderPanel({ catalogue, form, onChangeOrder, onRemoveProduct, onRemoveBundle, onOpenPreview, saving }) {
  const { selected_products: selectedProducts, bundles, layout_order: layoutOrder } = form;
  const [hoverKey, setHoverKey] = useState(null);
  const productById = useMemo(() => Object.fromEntries((catalogue?.products || []).map((p) => [p.id, p])), [catalogue]);
  const selectedFull = useMemo(
    () => selectedProducts.map((sp) => ({ ...sp, product: productById[sp.product_id] })).filter((sp) => sp.product),
    [selectedProducts, productById]
  );

  // Which fixed blocks the public page will actually show right now
  const hidden = useMemo(() => ({
    pricing: form.show_individual_pricing === false ? "Individual pricing is switched off" : selectedProducts.length === 0 ? "No products yet" : null,
    impact: form.roi?.enabled === false ? "Calculator switched off" : roiModes(form.roi).length === 0 ? "Both views switched off" : (bundles.length === 0 && !(Number(form.roi?.nichecom_cost_per_visit) > 0)) ? "Needs a package or a visit cost" : null,
    notes: form.notes?.trim() ? null : "Add notes to show this",
    getstarted: null,
    pricingRows: selectedProducts.length,
  }), [form.show_individual_pricing, form.roi, form.notes, selectedProducts.length, bundles.length]);

  // Resolve the saved key list against what's actually selected; anything new goes on the end.
  const items = useMemo(() => {
    const byKey = {};
    selectedProducts.forEach((sp) => {
      const p = productById[sp.product_id];
      byKey[productKey(sp.product_id)] = { key: productKey(sp.product_id), type: "product", id: sp.product_id, name: p?.name || sp.product_id, meta: p?.category || "Product", price: sp.nichecom_price };
    });
    bundles.forEach((b, i) => {
      byKey[bundleKey(b, i)] = { key: bundleKey(b, i), type: "bundle", id: b.id, idx: i, name: b.name || `Package ${i + 1}`, meta: "Package", price: b.price_without_epc };
    });
    FIXED_BLOCKS.forEach((def) => {
      byKey[def.key] = { key: def.key, type: "fixed", id: def.id, name: def.label, meta: def.desc, hidden: hidden[def.id] };
    });
    return resolveLayoutOrder(selectedFull, bundles, layoutOrder).map((k) => byKey[k]).filter(Boolean);
  }, [selectedProducts, bundles, layoutOrder, productById, selectedFull, hidden]);

  const { blocks } = useMemo(
    () => buildLayoutBlocks(selectedFull, bundles, items.map((it) => it.key)),
    [selectedFull, bundles, items]
  );
  // Row key → page block key (products roll up into their category section)
  const blockKeyFor = (rowKey) => {
    if (rowKey?.startsWith("product:")) {
      const sp = selectedFull.find((s) => productKey(s.product_id) === rowKey);
      return sp ? `section:${sp.product.category}` : null;
    }
    if (rowKey?.startsWith("bundle:")) return "packages";
    return rowKey;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (list) => onChangeOrder(list.map((it) => it.key));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    commit(arrayMove(items, items.findIndex((it) => it.key === active.id), items.findIndex((it) => it.key === over.id)));
  };
  const move = (from, to) => commit(arrayMove(items, from, to));
  const remove = (item) => (item.type === "bundle" ? onRemoveBundle(item.idx) : onRemoveProduct(item.id));
  const reset = () => onChangeOrder(recommendedLayoutOrder(selectedFull, bundles));

  // Clicking a preview block jumps to (the first row of) that block in the list
  const pickBlock = (blockKey) => {
    const row = items.find((it) => blockKeyFor(it.key) === blockKey);
    if (!row) return;
    setHoverKey(row.key);
    document.getElementById(`rdo-${row.key}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setTimeout(() => setHoverKey(null), 1200);
  };

  const hasPicks = selectedProducts.length > 0 || bundles.length > 0;

  return (
    <aside className="rdd-order" data-testid="rdd-order-panel">
      <div className="rdd-order-head">
        <span className="rdd-preview-label"><ListOrdered size={12} /> Proposal order</span>
        <button type="button" className="rdo-reset" onClick={reset} disabled={!hasPicks} title="Photography → Floorplans → Video → 3D → Social → EPCs, packages, then pricing · impact · notes · get started" data-testid="rdo-reset">
          <RotateCcw size={12} /> Recommended
        </button>
      </div>
      <p className="rdd-order-hint">The prospect's page follows this order. Drag or use the arrows — page blocks can go anywhere too.</p>
      {!hasPicks && (
        <div className="rdd-order-empty" data-testid="rdd-order-empty">Tick products and add packages on the left — they'll line up here.</div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((it) => it.key)} strategy={verticalListSortingStrategy}>
          <div className="rdd-order-list" data-testid="rdd-order-list">
            {items.map((it, idx) => (
              <OrderRow key={it.key} item={it} idx={idx} total={items.length} onMove={move} onRemove={remove} active={hoverKey != null && blockKeyFor(it.key) === blockKeyFor(hoverKey)} onHover={setHoverKey} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="rdd-order-preview-head">
        <span className="rdd-order-flow-label">Page preview</span>
        <span className="rdd-order-flow-sub">{blocks.filter((b) => !(b.type === "fixed" && hidden[b.id])).length} blocks · hover to match</span>
      </div>
      <ProposalPagePreview
        agencyName={form.agency_name}
        blocks={blocks}
        hidden={hidden}
        activeKey={blockKeyFor(hoverKey)}
        onHover={(k) => setHoverKey(k)}
        onPick={pickBlock}
      />
      <button type="button" className="rdd-order-open" onClick={onOpenPreview} disabled={saving || !form.agency_name?.trim()} title={form.agency_name?.trim() ? "Saves your changes, then opens the prospect's page in a new tab" : "Enter an agency name first"} data-testid="rdd-open-full-preview">
        {saving ? <Loader2 size={14} className="rdd-spin" /> : <ExternalLink size={14} />} Save &amp; open full preview
      </button>
    </aside>
  );
}
